import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Car,
  Info,
  User,
  Briefcase,
  FileText,
  Loader2
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { ENABLE_PHASE2_VERIFICATION } from '../featureFlags';

const API = `${process.env.REACT_APP_BACKEND_URL || '/_/backend'}/api`;

const Apply = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { lender, result, vehiclePrice, deposit, termMonths } = location.state || {};
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [lenders, setLenders] = useState([]);
  
  const [formData, setFormData] = useState({
    // Loan Details
    lender_id: lender?.lender_id || '',
    vehicle_price: vehiclePrice || 35000,
    deposit: deposit || 5000,
    term_months: termMonths || 60,
    
    // Vehicle Details
    vehicle_type: 'car',
    vehicle_year: new Date().getFullYear(),
    vehicle_make: '',
    vehicle_model: '',
    
    // Personal Details
    employment_status: '',
    annual_income: '',
    
    // Notes
    notes: ''
  });

  useEffect(() => {
    const fetchLenders = async () => {
      try {
        const res = await fetch(`${API}/lenders`);
        if (res.ok) {
          const data = await res.json();
          setLenders(data);
          setFormData(prev => (
            prev.lender_id || data.length === 0
              ? prev
              : { ...prev, lender_id: data[0].lender_id }
          ));
        }
      } catch (e) {
        console.error('Error fetching lenders:', e);
      }
    };
    fetchLenders();
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        return formData.lender_id && formData.vehicle_price > formData.deposit;
      case 2:
        return formData.vehicle_type && formData.vehicle_make && formData.vehicle_model;
      case 3:
        return formData.employment_status && formData.annual_income;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (!validateStep()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setStep(prev => prev + 1);
  };

  const handleBack = () => {
    setStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/applications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          lender_id: formData.lender_id,
          vehicle_price: parseFloat(formData.vehicle_price),
          deposit: parseFloat(formData.deposit),
          term_months: parseInt(formData.term_months),
          vehicle_type: formData.vehicle_type,
          vehicle_year: parseInt(formData.vehicle_year),
          vehicle_make: formData.vehicle_make,
          vehicle_model: formData.vehicle_model,
          employment_status: formData.employment_status,
          annual_income: parseFloat(formData.annual_income),
          notes: formData.notes
        })
      });

      if (res.ok) {
        toast.success('Application submitted successfully!');
        navigate('/dashboard');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to submit application');
      }
    } catch (e) {
      console.error('Error submitting application:', e);
      toast.error('Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency: 'NZD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const steps = [
    { num: 1, title: 'Loan Details', icon: FileText },
    { num: 2, title: 'Vehicle', icon: Car },
    { num: 3, title: 'Personal', icon: User },
    { num: 4, title: 'Review', icon: CheckCircle }
  ];

  const selectedLender = lenders.find(l => l.lender_id === formData.lender_id);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 pb-8 md:pt-32 md:pb-12 bg-gradient-to-br from-black via-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-4">
              Apply for Finance
            </h1>
            <p className="text-gray-400 text-lg max-w-xl mx-auto">
              Complete your application in just a few minutes
            </p>
          </motion.div>
        </div>
      </section>

      {/* Eligibility notice */}
      <section className="pt-6 pb-0">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-[#00D47E]/5 border border-[#00D47E]/20 rounded-xl p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-[#00D47E] shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-black">Online applications are currently handled by our team</p>
              <p className="text-xs text-gray-600 mt-1">
                Complete the{' '}
                <a href="/get-started" className="text-[#00D47E] hover:underline">eligibility check</a>{' '}
                instead — it takes about two minutes, and a finance specialist will call you to
                take it from there.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form — hidden while online applications are closed (GTM v1). Rendering a
          4-step form that 403s on submit would waste the user's time and teach them not to trust
          the page; the notice above is the whole message. */}
      {ENABLE_PHASE2_VERIFICATION && (
      <section data-testid="apply-section" className="py-12">
        <div className="max-w-3xl mx-auto px-6">
          {/* Progress Steps */}
          <div className="mb-10">
            <div className="flex items-center justify-between">
              {steps.map((s, idx) => (
                <div key={s.num} className="flex items-center">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors ${
                    step >= s.num 
                      ? 'bg-black border-black text-white' 
                      : 'border-gray-300 text-gray-400'
                  }`}>
                    {step > s.num ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <s.icon className="w-5 h-5" />
                    )}
                  </div>
                  {idx < steps.length - 1 && (
                    <div className={`hidden sm:block w-24 md:w-32 h-0.5 mx-2 ${
                      step > s.num ? 'bg-black' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>
            <div className="hidden sm:flex justify-between mt-2">
              {steps.map((s) => (
                <span key={s.num} className={`text-xs font-medium ${
                  step >= s.num ? 'text-black' : 'text-gray-400'
                }`}>
                  {s.title}
                </span>
              ))}
            </div>
          </div>

          {/* Form Content */}
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8"
          >
            {/* Step 1: Loan Details */}
            {step === 1 && (
              <div className="space-y-6">
                <h2 className="font-outfit text-2xl font-bold text-black mb-6">
                  Loan Details
                </h2>

                <div>
                  <Label htmlFor="lender" className="text-gray-700">Select Lender</Label>
                  <Select 
                    value={formData.lender_id} 
                    onValueChange={(val) => handleChange('lender_id', val)}
                  >
                    <SelectTrigger data-testid="lender-select" className="mt-2">
                      <SelectValue placeholder="Choose a lender" />
                    </SelectTrigger>
                    <SelectContent>
                      {lenders.map((l) => (
                        <SelectItem key={l.lender_id} value={l.lender_id}>
                          {l.name} ({l.min_rate}% - {l.max_rate}%)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="vehicle_price" className="text-gray-700">Vehicle Price</Label>
                    <Input
                      id="vehicle_price"
                      data-testid="vehicle-price-input"
                      type="number"
                      value={formData.vehicle_price}
                      onChange={(e) => handleChange('vehicle_price', e.target.value)}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="deposit" className="text-gray-700">Deposit</Label>
                    <Input
                      id="deposit"
                      data-testid="deposit-input"
                      type="number"
                      value={formData.deposit}
                      onChange={(e) => handleChange('deposit', e.target.value)}
                      className="mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="term" className="text-gray-700">Loan Term</Label>
                  <Select 
                    value={formData.term_months.toString()} 
                    onValueChange={(val) => handleChange('term_months', val)}
                  >
                    <SelectTrigger data-testid="term-select" className="mt-2">
                      <SelectValue placeholder="Select term" />
                    </SelectTrigger>
                    <SelectContent>
                      {[12, 24, 36, 48, 60, 72, 84].map((m) => (
                        <SelectItem key={m} value={m.toString()}>{m} months</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="bg-white rounded-lg p-4">
                  <p className="text-sm text-gray-600">
                    <strong>Loan Amount:</strong> {formatCurrency(formData.vehicle_price - formData.deposit)}
                  </p>
                </div>
              </div>
            )}

            {/* Step 2: Vehicle Details */}
            {step === 2 && (
              <div className="space-y-6">
                <h2 className="font-outfit text-2xl font-bold text-black mb-6">
                  Vehicle Details
                </h2>

                <div>
                  <Label htmlFor="vehicle_type" className="text-gray-700">Vehicle Type</Label>
                  <Select 
                    value={formData.vehicle_type} 
                    onValueChange={(val) => handleChange('vehicle_type', val)}
                  >
                    <SelectTrigger data-testid="vehicle-type-select" className="mt-2">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="car">Car</SelectItem>
                      <SelectItem value="suv">SUV</SelectItem>
                      <SelectItem value="ute">Ute</SelectItem>
                      <SelectItem value="van">Van</SelectItem>
                      <SelectItem value="motorcycle">Motorcycle</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="vehicle_year" className="text-gray-700">Year</Label>
                  <Select 
                    value={formData.vehicle_year.toString()} 
                    onValueChange={(val) => handleChange('vehicle_year', val)}
                  >
                    <SelectTrigger data-testid="vehicle-year-select" className="mt-2">
                      <SelectValue placeholder="Select year" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 15 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                        <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="vehicle_make" className="text-gray-700">Make</Label>
                  <Input
                    id="vehicle_make"
                    data-testid="vehicle-make-input"
                    placeholder="e.g., Toyota, Ford, Mazda"
                    value={formData.vehicle_make}
                    onChange={(e) => handleChange('vehicle_make', e.target.value)}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="vehicle_model" className="text-gray-700">Model</Label>
                  <Input
                    id="vehicle_model"
                    data-testid="vehicle-model-input"
                    placeholder="e.g., Corolla, Ranger, CX-5"
                    value={formData.vehicle_model}
                    onChange={(e) => handleChange('vehicle_model', e.target.value)}
                    className="mt-2"
                  />
                </div>
              </div>
            )}

            {/* Step 3: Personal Details */}
            {step === 3 && (
              <div className="space-y-6">
                <h2 className="font-outfit text-2xl font-bold text-black mb-6">
                  Personal Details
                </h2>

                <div>
                  <Label htmlFor="employment" className="text-gray-700">Employment Status</Label>
                  <Select 
                    value={formData.employment_status} 
                    onValueChange={(val) => handleChange('employment_status', val)}
                  >
                    <SelectTrigger data-testid="employment-select" className="mt-2">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_time">Full-time employed</SelectItem>
                      <SelectItem value="part_time">Part-time employed</SelectItem>
                      <SelectItem value="self_employed">Self-employed</SelectItem>
                      <SelectItem value="contractor">Contractor</SelectItem>
                      <SelectItem value="retired">Retired</SelectItem>
                      <SelectItem value="student">Student</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="income" className="text-gray-700">Annual Income (before tax)</Label>
                  <Input
                    id="income"
                    data-testid="income-input"
                    type="number"
                    placeholder="e.g., 65000"
                    value={formData.annual_income}
                    onChange={(e) => handleChange('annual_income', e.target.value)}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="notes" className="text-gray-700">Additional Notes (optional)</Label>
                  <Textarea
                    id="notes"
                    data-testid="notes-input"
                    placeholder="Any additional information..."
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    className="mt-2"
                    rows={4}
                  />
                </div>
              </div>
            )}

            {/* Step 4: Review */}
            {step === 4 && (
              <div className="space-y-6">
                <h2 className="font-outfit text-2xl font-bold text-black mb-6">
                  Review Your Application
                </h2>

                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4">
                    <h3 className="font-semibold text-black mb-2">Loan Details</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-gray-600">Lender:</span>
                      <span className="font-medium">{selectedLender?.name}</span>
                      <span className="text-gray-600">Vehicle Price:</span>
                      <span className="font-medium">{formatCurrency(formData.vehicle_price)}</span>
                      <span className="text-gray-600">Deposit:</span>
                      <span className="font-medium">{formatCurrency(formData.deposit)}</span>
                      <span className="text-gray-600">Loan Amount:</span>
                      <span className="font-medium">{formatCurrency(formData.vehicle_price - formData.deposit)}</span>
                      <span className="text-gray-600">Term:</span>
                      <span className="font-medium">{formData.term_months} months</span>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4">
                    <h3 className="font-semibold text-black mb-2">Vehicle</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-gray-600">Type:</span>
                      <span className="font-medium capitalize">{formData.vehicle_type}</span>
                      <span className="text-gray-600">Year:</span>
                      <span className="font-medium">{formData.vehicle_year}</span>
                      <span className="text-gray-600">Make:</span>
                      <span className="font-medium">{formData.vehicle_make}</span>
                      <span className="text-gray-600">Model:</span>
                      <span className="font-medium">{formData.vehicle_model}</span>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4">
                    <h3 className="font-semibold text-black mb-2">Personal</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-gray-600">Employment:</span>
                      <span className="font-medium capitalize">{formData.employment_status.replace('_', ' ')}</span>
                      <span className="text-gray-600">Annual Income:</span>
                      <span className="font-medium">{formatCurrency(formData.annual_income)}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-[#00D47E]/5 border border-gray-400 rounded-lg p-4">
                  <p className="text-sm text-gray-700">
                    <strong>What happens next?</strong> Once you submit, our team will review your application 
                    and contact you within 24 hours with your personalised rate and next steps.
                  </p>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
              {step > 1 ? (
                <Button
                  variant="outline"
                  onClick={handleBack}
                  data-testid="back-btn"
                  className="rounded-full"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  onClick={() => navigate('/calculator')}
                  data-testid="cancel-btn"
                  className="text-gray-600"
                >
                  Cancel
                </Button>
              )}

              {step < 4 ? (
                <Button
                  onClick={handleNext}
                  data-testid="next-btn"
                  className="bg-black hover:bg-gray-900 rounded-full"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  data-testid="submit-btn"
                  disabled={loading}
                  className="bg-[#00D47E] text-black hover:bg-[#00D47E]/80 rounded-full px-8"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Application
                      <CheckCircle className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </motion.div>
        </div>
      </section>
      )}

      <Footer />
    </div>
  );
};

export default Apply;
